import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { get, useFormContext, useFormState } from 'react-hook-form'
import { FieldSectionContext, UnsavedFieldsContext, textOf } from './field-meta'

// How FormNewLabel draws its label, hint and "required" line. Pages get the
// default (capitalised label, a "required" line under every required field)
// unless an ancestor provides a variant -- the SSO connection workspace does
// (shared/sso-workspace/workspace-shell.js): its labels are written in
// sentence case and semibold, hints are muted, and "required" appears only
// while the field is empty.
//   { groupClassName, labelClassName, subtextClassName, requiredClassName, requiredOnlyWhenEmpty, associateControl }
export const FormNewLabelVariant = createContext(null)

const DEFAULT_VARIANT = { groupClassName: 'form-group my-2 mx-1', labelClassName: 'text-capitalize', subtextClassName: undefined, requiredOnlyWhenEmpty: false, associateControl: false }

// secret: the value is write-only or sensitive; the unsaved-changes list
// says only "changed", never the value.
export const FormNewLabel = ({ children, name, label, subtext, required, hideLabel, secret }) => {
  const { watch } = useFormContext()
  const variant = { ...DEFAULT_VARIANT, ...useContext(FormNewLabelVariant) }

  // The unsaved-changes UI (opt-in: only under UnsavedChangesProvider).
  const unsaved = useContext(UnsavedFieldsContext)
  const section = useContext(FieldSectionContext)
  const { dirtyFields } = useFormState({ name })
  const edited = Boolean(unsaved) && Boolean(get(dirtyFields, name))
  const labelText = textOf(label || name)
  const register = unsaved && unsaved.register
  useEffect(
    () => {
      if (!register) return undefined
      return register(name, { label: labelText, sectionId: section && section.id, sectionTitle: section && section.title, secret: Boolean(secret) })
    },
    [register, name, labelText, section, secret]
  )

  const value = watch(name)

  const errorRequired = useMemo(() => required && !value, [required, value])

  const labelId = `${name}-label`
  // Real label/control association (label[for] + id), not only
  // aria-labelledby: aria-labelledby gives screen readers an accessible name,
  // but it does not make clicking the label text focus the control the way a
  // <label for> does, and a reviewer inspecting the DOM sees no `for` at all.
  // Opt-in per variant (the connection workspace turns it on) rather than
  // global: `name` becomes the control's id, and every workspace page keeps
  // one FormNew per route, so it can't collide with another field's id.
  const controlId = variant.associateControl ? name : null

  // Associate the visible <label> text with the actual control so
  // getByLabelText (and screen readers) can find it -- the label and its
  // children render as siblings here, not a wrapping <label>, so there is
  // no implicit association otherwise. Only single-element children can be
  // cloned; a field that already sets its own aria-label/aria-labelledby
  // (e.g. FormNewTags, which labels its inner <input> directly) is left
  // alone so this never fights a more specific association.
  const canAssociate = !hideLabel && React.isValidElement(children) &&
    !children.props['aria-label'] && !children.props['aria-labelledby']
  const labelledChildren = canAssociate
    ? React.cloneElement(children, { 'aria-labelledby': labelId, ...(controlId && !children.props.id ? { id: controlId } : {}) })
    : children

  return (
    <React.Fragment>
      <div className={`${variant.groupClassName}${edited ? ' sw-field-edited' : ''}`} data-field={name}>
        {!hideLabel && !unsaved &&
          <label id={labelId} htmlFor={controlId || undefined} className={variant.labelClassName}>
            {label || name}
          </label>}
        {!hideLabel && unsaved &&
          <div className="sw-label-row">
            <label id={labelId} htmlFor={controlId || undefined} className={variant.labelClassName}>
              {label || name}
            </label>
            {edited && <span className="sw-edited" data-testid={`edited-${name}`}>Edited</span>}
          </div>}
        <div>
          {labelledChildren}
        </div>
        {subtext &&
          <small className={variant.subtextClassName}>
            {subtext}
          </small>}
        {variant.requiredOnlyWhenEmpty
          ? errorRequired && <small className={variant.requiredClassName}>required</small>
          : <div>
            {required && <FormErrorRequired errorRequired={errorRequired} />}
          </div>}
      </div>
    </React.Fragment>
  )
}

const FormErrorRequired = ({ errorRequired }) => {
  return (
    <React.Fragment>
      <div>
        <small className={errorRequired ? 'text-danger' : 'text-muted'}>required</small>
      </div>
    </React.Fragment>
  )
}
